import { Skeleton } from '@mantine/core'
import { useStatsigClient } from '@statsig/react-bindings'
import { ReactQuery } from 'cms-api'
import { type PromoCodeIncludedBrandsItem } from 'cms-api/src/axios/cms'
import { Formik } from 'formik'
import { get } from 'lodash'
import React, { useEffect, useState } from 'react'
import { CUSTOM_CONTENT, LEAD_TIME_MAX_ORDERABLE_QTY } from 'shared-constants'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import { ProductQuantityCounterField } from '@public/components/ProductQuantityCounterField'
import { useProductVariantQuantity } from '@public/hooks'
import { Box } from 'components/Layout'
import { type CartContextInterface } from 'providers/cart'
import { useGoogleAnalytics } from 'providers/googleAnalytics'
import { STATSIG_EVENTS } from 'providers/statsig'
import { useUserAuth } from 'providers/userAuth'
import { FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'
import { catchHandler } from 'utils/catchHandler'
import { klaviyoTrackAddToCartShowroom } from 'utils/klaviyo'
import { getVariantsLeadTime, onAddItemToCartTracking } from '../../utils'
import { AddToCartButton } from './AddToCartButton'
import {
  getProductHeaderValidationSchema,
  PRODUCT_HEADER_FIELD_NAMES,
} from './productHeaderValidationSchema'
import { Title } from './Title'
import { VariantDescription } from './VariantDescription'
import { VariantDetails } from './VariantDetails'
import { VariantPickerLegacy, VariantPickerTypesense } from './VariantPicker'

const trackAddOnCartHandler = ({
  productId,
  productVariantId,
  productVariantSku,
}) => {
  klaviyoTrackAddToCartShowroom({
    productId,
    productVariantId,
    productVariantSku,
  })
}

interface ProductHeaderBaseProps {
  isLoading: boolean
  cartQuantity: number
  openCartModal: ({ addedQuantity }: { addedQuantity: number }) => void
  isSetItemToCartLoading: boolean
  setItem: CartContextInterface['setItem']
  detailsRef: React.RefObject<HTMLDivElement>
  cartId: number
  variantPicker: React.ReactNode
  isPreview: boolean
}

export const ProductHeaderBase: React.FC<
  ProductHeaderBaseProps &
    Pick<React.ComponentProps<typeof AddToCartButton>, 'availability'> &
    Omit<React.ComponentProps<typeof VariantDetails>, 'dimensionUnit'> &
    Omit<
      React.ComponentProps<typeof VariantDescription>,
      | 'availableQuantity'
      | 'minimumCounterValue'
      | 'isPriceUponRequest'
      | 'hasPublishedStatus'
      | 'hasCustomization'
      | 'location'
    > &
    Omit<
      React.ComponentProps<typeof Title>,
      'isPriceVisible' | 'showroom' | 'discount' | 'id'
    > &
    React.PWC<{
      productVariantStatus:
        | 'NOT_PUBLISHED'
        | 'PUBLISHED'
        | 'ARCHIVED'
        | 'PENDING_APPROVAL'
      productId: number
      title: string
      productVariantId: number
      sku: string
      quantity: number
      isCounterEnabled: boolean
      quantityUnitPlural: string
      aboveTheFold?: string
    }>
> = ({
  productVariantStatus,
  productId,
  title,
  brand,
  brandSlug,
  leadTimeInWeeks,
  availability,
  productVariantId,
  quantity,
  sku,
  tradePrice,
  price,
  cartQuantity,
  isCounterEnabled,
  minimumQuantity,
  quantityUnitSingular,
  quantityUnitPlural,
  isPreview,
  customProductDescription,
  customProductHeadline,
  aboveTheFold,
  detailsRef,
  enquire,
  isSetItemToCartLoading,
  variantPicker,
  openCartModal,
  isLoading,
  setItem,
  cartId,
  accordionCare,
  accordionDetails,
  customDimensions,
  isEligibleForProposition65,
  proposition65,
  depth,
  height,
  length,
  accordionShipping,
  width,
}) => {
  const { executeDataToDataLayer } = useGoogleAnalytics()
  const { user, isPageLoading } = useUserAuth()
  const { client: statsigClient } = useStatsigClient()

  const variantQuantity = leadTimeInWeeks
    ? LEAD_TIME_MAX_ORDERABLE_QTY
    : quantity
  const availableQuantity = variantQuantity - cartQuantity

  const isPriceUponRequest = price === 0
  const { data: blackFridayPromoCode } =
    ReactQuery.useGetPromoCodesBlackFriday()

  const hasPublishedStatus = productVariantStatus === 'PUBLISHED'

  // This is a temporary fix which will be further addressed in https://github.com/sudolabs-io/the-expert/issues/6172
  const isArchived = productVariantStatus === 'ARCHIVED'

  // Belt-and-suspenders for cases where the server-side variant status
  // (legacy DB) disagrees with the page's status prop (e.g. stale Typesense
  // index). The cart mutation returns "Invalid product variant" when the
  // legacy variant isn't PUBLISHED; on that error we flip into the same UX
  // an explicitly-archived variant gets, so the customer can't keep clicking.
  const [isServerRejected, setIsServerRejected] = useState(false)
  useEffect(() => {
    setIsServerRejected(false)
  }, [productVariantId])

  // This ensures that if you have more than minimumQuantity in cart, you can add whatever quantity you want to cart
  const minimumCounterValue =
    cartQuantity >= minimumQuantity ? 1 : minimumQuantity

  const hasCustomization =
    Boolean(customProductDescription) && Boolean(customProductHeadline)

  const includedBrands = (blackFridayPromoCode?.data?.includedBrands ?? []).map(
    ({ name }: PromoCodeIncludedBrandsItem & { name: string }) => name
  )
  const shouldDisplayBlackFridayBadge =
    brand &&
    availability &&
    !isPageLoading &&
    !user?.isTradeCapable &&
    availability !== 'SOLD_OUT' &&
    includedBrands.includes(brand)

  const quantityUnit =
    quantityUnitSingular && quantityUnitPlural
      ? {
          singular: quantityUnitSingular,
          plural: quantityUnitPlural,
        }
      : undefined

  return (
    <>
      <Title
        id={productVariantId}
        brand={brand}
        title={title}
        price={price}
        tradePrice={tradePrice}
        quantityUnitSingular={quantityUnitSingular}
        isPriceVisible={hasPublishedStatus || isPreview}
        discount={
          shouldDisplayBlackFridayBadge
            ? `Use code ${blackFridayPromoCode.data.code} for Black Friday discount`
            : undefined
        }
        isLoading={isLoading}
      />

      <VariantDescription
        availableQuantity={availableQuantity}
        minimumQuantity={minimumCounterValue}
        detailsRef={detailsRef}
        isPriceUponRequest={isPriceUponRequest}
        aboveTheFold={aboveTheFold}
        leadTimeInWeeks={leadTimeInWeeks}
        availability={availability}
        enquire={enquire}
        hasPublishedStatus={hasPublishedStatus}
        hasCustomization={hasCustomization}
      />

      {variantPicker}

      <Formik
        onSubmit={async ({ quantity: addedQuantity }, { resetForm }) => {
          const response = await setItem({
            quantity: cartQuantity + addedQuantity,
            productVariantId,
          })
          if (
            response.errors?.some(
              (e) => e.message === 'Invalid product variant'
            )
          ) {
            setIsServerRejected(true)
          }
          if (!response.errors && !isLoading) {
            onAddItemToCartTracking({
              executeDataToDataLayer,
              quantity: addedQuantity,
              productVariant: {
                id: productVariantId,
                sku,
                title,
              } as never,
              cartId: cartId || get(response, ['data', 'setItemToCart', 'id']),
              itemListName: get(
                response,
                [
                  'data',
                  'setItemToCart',
                  'items',
                  '0',
                  'metadata',
                  'ga4',
                  'listName',
                ],
                null
              ),
            })
            if (statsigClient) {
              statsigClient.logEvent(STATSIG_EVENTS.ADD_TO_CART, sku, {
                isTradeCapable: user?.isTradeCapable ? 'true' : 'false',
                quantity: `${cartQuantity + addedQuantity}`,
              })
            }
            openCartModal({ addedQuantity })
            resetForm({
              values: {
                quantity: 1,
              },
            })
            if (user?.contactEmail) {
              catchHandler(trackAddOnCartHandler)({
                productId,
                productVariantId,
                productVariantSku: sku,
              })
            }
          }
        }}
        initialValues={{
          quantity: minimumCounterValue,
        }}
        validationSchema={getProductHeaderValidationSchema({
          availableQuantity,
          minimumOrderQuantity: minimumCounterValue,
          leadTime: leadTimeInWeeks,
        })}
        validateOnChange
        enableReinitialize
      >
        {({ handleSubmit }) => (
          <>
            {(isCounterEnabled || minimumQuantity > 1) &&
              hasPublishedStatus &&
              availableQuantity > minimumCounterValue && (
                <Box mb={SPACE.PX_20}>
                  <ProductQuantityCounterField
                    data-test-id={
                      DATA_TEST_CLASS.SHOWROOM_PRODUCT_DETAIL_COUNTER
                    }
                    id={productVariantId.toString()}
                    name={PRODUCT_HEADER_FIELD_NAMES.QUANTITY}
                    minimumOrderQuantity={minimumCounterValue}
                    availableQuantity={availableQuantity}
                    quantityUnit={quantityUnit}
                    textProps={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: FONT_SIZE.PX_12,
                      fontWeight: FONT_WEIGHT.MEDIUM,
                      mt: SPACE.PX_8,
                    }}
                  />
                </Box>
              )}
            <AddToCartButton
              productVariantId={productVariantId}
              productId={productId}
              isAddToCartDisabled={variantQuantity <= cartQuantity}
              isAddToCartLoading={isSetItemToCartLoading}
              isPriceUponRequest={isPriceUponRequest}
              onAddToCart={handleSubmit}
              isSoldOut={
                variantQuantity === 0 ||
                minimumCounterValue > variantQuantity ||
                isArchived ||
                isServerRejected
              }
              width="100%"
              isVariantPublished={
                (hasPublishedStatus || isPreview) && !isServerRejected
              }
              availability={availability}
            />
          </>
        )}
      </Formik>
      <VariantDetails
        customDimensions={customDimensions}
        proposition65={proposition65}
        isEligibleForProposition65={isEligibleForProposition65}
        accordionCare={accordionCare}
        depth={depth}
        height={height}
        length={length}
        accordionShipping={accordionShipping}
        width={width}
        brand={brand}
        detailsRef={detailsRef}
        brandSlug={brandSlug}
        customProductDescription={customProductDescription}
        customProductHeadline={customProductHeadline}
        accordionDetails={accordionDetails}
      />
    </>
  )
}

export const ProductHeaderLegacy: React.FC<
  React.PWC<
    Omit<ProductHeaderBaseProps, 'variantPicker'> &
      React.ComponentProps<typeof VariantPickerLegacy> & {
        product: PublicProductQuery['product']
      }
  >
> = ({ product, selectedVariant, variants, onVariantChange, ...rest }) => {
  const { id: productId, title, brand } = product
  const {
    id,
    sku,
    price,
    tradePrice,
    quantity,
    managedCustomFields,
    availability,
    customContent,
  } = selectedVariant
  const leadTimeInWeeks = getVariantsLeadTime({
    managedCustomFields,
  })
  const { isCounterEnabled, minimumOrderQuantity, quantityUnit } =
    useProductVariantQuantity({ managedCustomFields })

  const aboveTheFold = get(selectedVariant, [
    'customContent',
    CUSTOM_CONTENT.ABOVE_THE_FOLD,
  ])

  const customProductDescription = get(
    customContent,
    'customProductDescription',
    null
  )
  const customProductHeadline = get(
    customContent,
    'customProductHeadline',
    null
  )

  const accordionDetails = selectedVariant.customContent?.accordionDetails
  const customDimensions = selectedVariant.customContent?.customDimensions
  const isEligibleForProposition65 =
    selectedVariant.managedCustomFields?.prop65Eligible
  const proposition65 = selectedVariant.managedCustomFields?.proposition65
  const accordionShipping = selectedVariant.customContent?.accordionShipping
  const accordionCare = selectedVariant.customContent?.accordionCare
  const { depth } = selectedVariant.managedCustomFields
  const { width, length, height } = selectedVariant

  return (
    <ProductHeaderBase
      {...rest}
      isCounterEnabled={isCounterEnabled}
      minimumQuantity={minimumOrderQuantity}
      price={price.total}
      productId={productId}
      productVariantId={id}
      productVariantStatus={selectedVariant?.status}
      quantity={quantity}
      quantityUnitPlural={quantityUnit?.plural}
      quantityUnitSingular={quantityUnit?.singular}
      sku={sku}
      title={title}
      tradePrice={tradePrice.total}
      brand={brand?.id}
      brandSlug={brand?.cmsBrandPage.cmsPage.slug}
      aboveTheFold={aboveTheFold}
      accordionCare={accordionCare}
      accordionDetails={accordionDetails}
      availability={availability as never}
      customDimensions={customDimensions}
      isEligibleForProposition65={isEligibleForProposition65}
      customProductDescription={customProductDescription}
      customProductHeadline={customProductHeadline}
      depth={depth}
      enquire={selectedVariant.customContent?.enquire}
      height={height}
      leadTimeInWeeks={leadTimeInWeeks}
      length={length}
      proposition65={proposition65}
      accordionShipping={accordionShipping}
      width={width}
      variantPicker={
        <VariantPickerLegacy
          variants={variants}
          selectedVariant={selectedVariant}
          onVariantChange={onVariantChange}
        />
      }
    />
  )
}

export const ProductHeaderTypesense: React.FC<
  React.PWC<
    Omit<ProductHeaderBaseProps, 'variantPicker'> &
      React.ComponentProps<typeof VariantPickerTypesense>
  >
> = ({ selectedVariant, variants, onVariantChange, isLoading, ...rest }) =>
  selectedVariant ? (
    <ProductHeaderBase
      {...rest}
      isCounterEnabled={selectedVariant.isCounterEnabled}
      minimumQuantity={selectedVariant.minimumQuantity}
      price={selectedVariant.price}
      productId={selectedVariant.legacyProductId}
      productVariantId={selectedVariant.legacyProductVariantId}
      productVariantStatus={selectedVariant.status}
      quantity={selectedVariant.quantity}
      quantityUnitPlural={selectedVariant.quantityUnitPlural}
      quantityUnitSingular={selectedVariant.quantityUnitSingular}
      sku={selectedVariant.sku}
      title={selectedVariant.title}
      tradePrice={selectedVariant.tradePrice}
      brand={
        selectedVariant.brandSlug.includes('nobrand')
          ? undefined
          : selectedVariant.brand
      }
      brandSlug={selectedVariant.brandSlug}
      aboveTheFold={selectedVariant.aboveTheFold}
      accordionCare={selectedVariant.accordionCare}
      accordionDetails={selectedVariant.accordionDetails}
      availability={selectedVariant.availability}
      customDimensions={selectedVariant.customDimensions}
      isEligibleForProposition65={selectedVariant.isEligibleForProposition65}
      customProductDescription={selectedVariant.customProductDescription}
      customProductHeadline={selectedVariant.customProductHeadline}
      depth={selectedVariant.depth}
      enquire={selectedVariant.enquire}
      height={selectedVariant.height}
      leadTimeInWeeks={selectedVariant.leadTimeInWeeks}
      length={selectedVariant.length}
      proposition65={selectedVariant.proposition65}
      accordionShipping={selectedVariant.accordionShipping}
      width={selectedVariant.width}
      variantPicker={
        isLoading ? (
          <Skeleton height={100} width="100%" radius="8px" bg="lightgray" />
        ) : (
          <VariantPickerTypesense
            onVariantChange={onVariantChange}
            variants={variants}
            selectedVariant={selectedVariant}
          />
        )
      }
      isLoading={isLoading}
    />
  ) : null
